/**
 * LinkPickerModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import './LinkPickerModal.css';

const LinkPickerModal = ({ isOpen, onClose, onInsertLink }) => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('navigation');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [contentPages, setContentPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [includeProductImage, setIncludeProductImage] = useState(false);

  // Navigation pages from the app routes
  const navigationPages = [
    { path: '/', label: 'Home' },
    { path: '/shop', label: 'Shop' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
    { path: '/tunes', label: 'Tunes' },
    { path: '/articles', label: 'Articles' },
    { path: '/privacy-policy', label: 'Privacy Policy' },
    { path: '/terms-of-use', label: 'Terms of Use' },
    { path: '/return-policy', label: 'Return Policy' },
    { path: '/shipping-policy', label: 'Shipping Policy' },
  ];

  // Fetch products when products tab is selected
  useEffect(() => {
    if (activeTab === 'products' && products.length === 0) {
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch content pages when pages tab is selected
  useEffect(() => {
    if (activeTab === 'pages' && contentPages.length === 0) {
      fetchContentPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/products');
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContentPages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/content/pages', { headers });
      if (response.ok) {
        const data = await response.json();
        setContentPages(data);
      } else if (response.status === 401 || response.status === 403) {
        // Not authenticated - show helpful message
        setContentPages([]);
      }
    } catch (error) {
      console.error('Error fetching content pages:', error);
      setContentPages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = (url, defaultLabel, productData = null) => {
    const text = linkText.trim() || defaultLabel;
    
    // If inserting a product with image option enabled
    if (productData && includeProductImage && productData.image) {
      const imageUrl = productData.image.startsWith('/') ? productData.image : `/${productData.image}`;
      const imageHtml = `<div style="display: inline-block; margin: 0.5rem; max-width: 150px;">
  <a href="${url}" style="text-decoration: none; display: block;">
    <img src="${imageUrl}" alt="${text}" style="max-width: 150px !important; width: 150px !important; height: auto !important; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: block; margin-bottom: 0.5rem; object-fit: cover;" />
    <div style="text-align: center; color: #333; font-weight: 500; font-size: 0.85rem; line-height: 1.2;">${text}</div>
  </a>
</div>`;
      onInsertLink(null, imageHtml); // Pass null as url to insert raw HTML
    } else {
      onInsertLink(url, text);
    }
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setLinkText('');
    setIncludeProductImage(false);
    setActiveTab('navigation');
    onClose();
  };

  const filterItems = (items, labelKey) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      item[labelKey]?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredNavigation = filterItems(navigationPages, 'label');
  const filteredProducts = filterItems(products, 'name');
  const filteredPages = filterItems(contentPages, 'title');

  if (!isOpen) return null;

  // Theme-aware styles
  const modalStyle = {
    background: theme.colors['--color-background'],
    color: theme.colors['--color-text-primary'],
  };

  const headerStyle = {
    borderBottomColor: theme.colors['--color-border'],
  };

  const tabStyle = (isActive) => ({
    color: isActive ? theme.colors['--color-primary'] : theme.colors['--color-text-secondary'],
    borderBottomColor: isActive ? theme.colors['--color-primary'] : 'transparent',
  });

  const itemStyle = {
    background: theme.colors['--color-background-secondary'] || theme.colors['--color-background'],
    borderColor: theme.colors['--color-border'],
  };

  const buttonStyle = {
    background: theme.colors['--color-primary'],
    color: '#fff',
  };

  return (
    <div className="link-picker-overlay" onClick={handleClose}>
      <div className="link-picker-modal" onClick={(e) => e.stopPropagation()} style={modalStyle}>
        <div className="link-picker-header" style={headerStyle}>
          <h3>Insert Link</h3>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="link-picker-tabs" style={{ borderBottomColor: theme.colors['--color-border'] }}>
          <button
            className={`tab-btn ${activeTab === 'navigation' ? 'active' : ''}`}
            onClick={() => setActiveTab('navigation')}
            style={tabStyle(activeTab === 'navigation')}
          >
            📍 Navigation
          </button>
          <button
            className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
            style={tabStyle(activeTab === 'products')}
          >
            🛍️ Products
          </button>
          <button
            className={`tab-btn ${activeTab === 'pages' ? 'active' : ''}`}
            onClick={() => setActiveTab('pages')}
            style={tabStyle(activeTab === 'pages')}
          >
            📄 Pages
          </button>
          <button
            className={`tab-btn ${activeTab === 'custom' ? 'active' : ''}`}
            onClick={() => setActiveTab('custom')}
            style={tabStyle(activeTab === 'custom')}
          >
            🔗 Custom URL
          </button>
        </div>

        <div className="link-picker-search" style={{ borderBottomColor: theme.colors['--color-border'] }}>
          {activeTab !== 'custom' && (
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{
                background: theme.colors['--color-background'],
                color: theme.colors['--color-text-primary'],
                borderColor: theme.colors['--color-border'],
              }}
            />
          )}
        </div>

        <div className="link-picker-content">
          {/* Navigation Pages */}
          {activeTab === 'navigation' && (
            <div className="link-list">
              {filteredNavigation.length > 0 ? (
                filteredNavigation.map((page, index) => (
                  <div key={index} className="link-item" style={itemStyle}>
                    <div className="link-info">
                      <div className="link-label" style={{ color: theme.colors['--color-text-primary'] }}>{page.label}</div>
                      <div className="link-path" style={{ color: theme.colors['--color-text-secondary'] }}>{page.path}</div>
                    </div>
                    <button
                      className="insert-btn"
                      onClick={() => handleInsert(page.path, page.label)}
                      style={buttonStyle}
                    >
                      Insert
                    </button>
                  </div>
                ))
              ) : (
                <div className="no-results" style={{ color: theme.colors['--color-text-secondary'] }}>No pages found</div>
              )}
            </div>
          )}

          {/* Products */}
          {activeTab === 'products' && (
            <div className="link-list">
              {loading ? (
                <div className="loading" style={{ color: theme.colors['--color-text-secondary'] }}>Loading products...</div>
              ) : filteredProducts.length > 0 ? (
                <>
                  <div className="product-options" style={{ 
                    background: `${theme.colors['--color-primary']}15`,
                    borderColor: theme.colors['--color-primary'],
                  }}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={includeProductImage}
                        onChange={(e) => setIncludeProductImage(e.target.checked)}
                      />
                      <span style={{ color: theme.colors['--color-text-primary'] }}>Include product thumbnail image</span>
                    </label>
                  </div>
                  {filteredProducts.map((product) => (
                    <div key={product.id} className="link-item" style={itemStyle}>
                      {product.image && (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="product-thumbnail"
                        />
                      )}
                      <div className="link-info">
                        <div className="link-label" style={{ color: theme.colors['--color-text-primary'] }}>{product.name}</div>
                        <div className="link-path" style={{ color: theme.colors['--color-text-secondary'] }}>/product/{product.id}</div>
                        {product.category && (
                          <div className="link-meta" style={{ color: theme.colors['--color-text-tertiary'] }}>{product.category}</div>
                        )}
                      </div>
                      <button
                        className="insert-btn"
                        onClick={() => handleInsert(`/product/${product.id}`, product.name, product)}
                        title={includeProductImage && product.image ? "Insert with thumbnail" : "Insert link only"}
                        style={buttonStyle}
                      >
                        {includeProductImage && product.image ? '🖼️ Insert' : 'Insert'}
                      </button>
                    </div>
                  ))}
                </>
              ) : (
                <div className="no-results" style={{ color: theme.colors['--color-text-secondary'] }}>No products found</div>
              )}
            </div>
          )}

          {/* Content Pages */}
          {activeTab === 'pages' && (
            <div className="link-list">
              {loading ? (
                <div className="loading" style={{ color: theme.colors['--color-text-secondary'] }}>Loading pages...</div>
              ) : filteredPages.length > 0 ? (
                filteredPages.map((page) => (
                  <div key={page.page_id} className="link-item" style={itemStyle}>
                    <div className="link-info">
                      <div className="link-label" style={{ color: theme.colors['--color-text-primary'] }}>{page.title}</div>
                      <div className="link-path" style={{ color: theme.colors['--color-text-secondary'] }}>/page/{page.page_id}</div>
                      {page.page_type && (
                        <div className="link-meta" style={{ color: theme.colors['--color-text-tertiary'] }}>{page.page_type}</div>
                      )}
                    </div>
                    <button
                      className="insert-btn"
                      onClick={() => handleInsert(`/page/${page.page_id}`, page.title)}
                      style={buttonStyle}
                    >
                      Insert
                    </button>
                  </div>
                ))
              ) : (
                <div className="no-results" style={{ color: theme.colors['--color-text-secondary'] }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: theme.colors['--color-text-primary'] }}>Page Management Required</strong>
                  </div>
                  <p>Custom pages can be managed through the admin panel:</p>
                  <p style={{ fontFamily: 'monospace', marginTop: '0.5rem' }}>
                    Admin → Page Management
                  </p>
                  <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                    💡 Tip: Use the <strong style={{ color: theme.colors['--color-text-primary'] }}>Custom URL</strong> tab to manually enter a page link if you know the page ID.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Custom URL */}
          {activeTab === 'custom' && (
            <div className="custom-url-form">
              <div className="form-group">
                <label style={{ color: theme.colors['--color-text-primary'] }}>URL:</label>
                <input
                  type="text"
                  placeholder="https://example.com or /path"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="url-input"
                  style={{
                    background: theme.colors['--color-background'],
                    color: theme.colors['--color-text-primary'],
                    borderColor: theme.colors['--color-border'],
                  }}
                />
              </div>
              <div className="form-group">
                <label style={{ color: theme.colors['--color-text-primary'] }}>Link Text:</label>
                <input
                  type="text"
                  placeholder="Link text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="text-input"
                  style={{
                    background: theme.colors['--color-background'],
                    color: theme.colors['--color-text-primary'],
                    borderColor: theme.colors['--color-border'],
                  }}
                />
              </div>
              <button
                className="insert-custom-btn"
                onClick={() => handleInsert(searchTerm, linkText || searchTerm)}
                disabled={!searchTerm.trim() || !linkText.trim()}
                style={{
                  background: (!searchTerm.trim() || !linkText.trim()) ? theme.colors['--color-text-tertiary'] : theme.colors['--color-success'] || '#28a745',
                  color: '#fff',
                }}
              >
                Insert Link
              </button>
            </div>
          )}
        </div>

        {activeTab !== 'custom' && (
          <div className="link-picker-footer" style={{ 
            borderTopColor: theme.colors['--color-border'],
            background: theme.colors['--color-background-secondary'] || theme.colors['--color-background'],
          }}>
            <div className="form-group">
              <label style={{ color: theme.colors['--color-text-primary'] }}>Link Text (optional - will use page title if empty):</label>
              <input
                type="text"
                placeholder="Custom link text..."
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                className="text-input"
                style={{
                  background: theme.colors['--color-background'],
                  color: theme.colors['--color-text-primary'],
                  borderColor: theme.colors['--color-border'],
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkPickerModal;

